import { takeEvery, takeLeading, debounce, call, spawn, select, put, all, delay } from 'redux-saga/effects';
import * as actions from './actions.js';
import * as selectors from './selectors.js';
import * as auth from '../auth/index.js';
import * as router from '../router/index.js';
import * as manageAccountSelectors from '../manage-account/selectors.js';
import firestoreRedux from '@dreamworld/firestore-redux';
import { requestApi, isNetworkError } from '../../request-api.js';
import { show as showSnackBar } from '../../components/kerika-snackbar.js';
import { getIdWoPrefix } from '../../utils.js';
import i18next from '@dw/i18next-esm';
import get from 'lodash-es/get.js';

/**
 * All sagas read state with `const state = yield select()` and then call selectors
 * with it - never `yield select(selectorFn)`.
 */

/**
 * Requester for the running timer's card. Deliberately not account-scoped: the pages
 * that need it (Explore, Home) have no account in the URL, so an account-scoped
 * requester would key on an empty string.
 */
const ACTIVE_TIMER_CARD_REQUESTER = 'time-tracker-active-card';

/**
 * Requester for the user's running timers. Not account-scoped: the query spans every
 * account, and the pages that need it most have no account in the URL.
 */
const ACTIVE_TIMERS_REQUESTER = 'time-tracker-active-timers';

/** Requester prefix for the live read on each running timer's card - see `watchTimedCardsSaga`. */
const TIMED_CARD_REQUESTER_PREFIX = 'time-tracker-timed-card-';

let lastLoadedUserId;

const PROMPT_DEBOUNCE_MS = 500;

/** The cards `watchTimedCardsSaga` keeps a live read on. */
let watchedTimedCardIds = [];

/**
 * The admin key from the connect step, held only until the activate step that follows it.
 *
 * Module scope on purpose: an admin who picks "use this admin connection for me too" is
 * linked with the key they just typed, and asking them to type it a second time one
 * dialog later would be absurd. But a credential does not belong in redux - devtools, the
 * action log and any state dump would all carry it - nor in storage, so it lives here,
 * for the seconds between the two steps, and is dropped as soon as it is used.
 *
 * Lost on reload, which is the safe direction to fail: the admin is simply asked for a
 * key when they first start a timer, exactly as any other member is.
 */
let pendingAdminApiKey;

/**
 * The account a *timer* operation applies to: the one asked for, else the one in the
 * URL, else the account of the timer that is actually running.
 *
 * That last fallback is what makes the header pill work on Home/Explore, which has no
 * account in its path - stopping from there would otherwise post to
 * `/timetracker//stop`. Account-level operations (connect, settings) deliberately do
 * not use this: they are only ever reached from a page that has an account.
 */
const timerAccountId = (state, accountId) =>
  accountId || router.selectors.accountId(state) || get(selectors.active(state), 'accountId');

/**
 * The statuses this module answers for itself.
 *
 * `request-api` raises its own "API failed. status-code: 409" toast for anything a caller
 * has not claimed, which is both meaningless to the user and, now that these sagas report
 * their own failures, a second toast on top of the useful one. Every business error the
 * tracker API returns arrives as one of these three - 400 validation, 404 never connected,
 * 409 everything else - so the module claims all three and does the talking itself.
 */
/**
 * `card-details`' own "the card's attributes changed" action, named here rather than
 * imported.
 *
 * Importing `card-details/actions.js` pulls its selectors in with it, and that closes a
 * loop through the store: the app booted to `Cannot access 'sagaMiddleware' before
 * initialization` and never started. A string constant costs one duplicated literal and
 * cannot reorder anyone's module graph.
 *
 * Kept in step with `redux/card-details/actions.js`'s `UPDATE_ATTRS`.
 */
const CARD_DETAILS_UPDATE_ATTRS = 'CARD_DETAIL_UPDATE_CARD_ATTRS';

/**
 * The other attributes `CARD_DETAIL_UPDATE_CARD_ATTRS` can carry.
 *
 * That one action covers the title, the description, the due date, the priority and the
 * status, so seeing it says nothing on its own about what changed. `updateStatus` sends
 * the status and nothing else, which is what distinguishes a status change from an edit
 * that merely happens to mention one - and the reason this is a shape check rather than a
 * before/after comparison is that both sagas take the same action, so whether the stored
 * card still holds the old status depends on which of them ran first.
 */
const CARD_DETAILS_OTHER_ATTRS = ['title', 'descriptionHtml', 'currentDescriptionHtml', 'dueDate', 'priority'];

/** @returns {Boolean} `true` when this update changed the status and nothing else. */
const isStatusOnlyChange = (action) =>
  CARD_DETAILS_OTHER_ATTRS.every((attr) => get(action, attr) === undefined);

const HANDLED_STATUSES = [400, 404, 409];

/** The one API entry point for this module. Resolves with the response body, rejects with an error carrying a `code`. */
const callApi = requestApi;

/** Optimistic local write, ahead of the server's own answer. */
const saveDoc = (collection, doc) => firestoreRedux.save(collection, doc, { localWrite: true });

const deleteDoc = (collection, id) => firestoreRedux.delete(collection, id, { localWrite: true });

/**
 * `showSnackBar` throws when the app shell has not mounted its `<snack-bar>` yet - an
 * unhandled throw here would escape a saga's catch block, and redux-saga tears down
 * the whole root saga with it (every `takeEvery` in this module, for the rest of the
 * session). Failing to show a toast must never cost more than the toast.
 */
const safeShowErrorToast = (messageKey, values) => {
  try {
    showSnackBar({ type: 'ERROR', message: i18next.t(messageKey, values) });
  } catch (error) {
    console.warn('Time tracker > could not show a toast: ', error);
  }
};

/**
 * The codes a timer operation can fail with that the *user* can do something about, and
 * what to tell them. Anything not listed here is either handled at its own call site,
 * because it does more than raise a toast (a dialog, a route), or is genuinely unexpected.
 *
 * Two of these read differently depending on who is looking: an admin can go and fix the
 * connection, a member can only go and ask someone.
 */
const TIMER_ERROR_TOASTS = {
  // The tracker's own explanation ('connection refused', a timeout, a 5xx) is likewise
  // forwarded as-is here - see the comment on VALIDATION_FAILED below for why.
  PROVIDER_UNAVAILABLE: () => 'time-tracker:toasts.providerUnavailable',
  NO_TIMER_RUNNING: () => 'time-tracker:toasts.noTimerRunning',
  // The entry this timer points at is gone from the tracker, so stopping it is not
  // something a retry can achieve. The dialog stays open and the message names Discard,
  // which is already on it and does succeed against an entry that is already gone.
  ENTRY_NOT_FOUND: () => 'time-tracker:toasts.entryNotFound',
  DESCRIPTION_REQUIRED: () => 'time-tracker:toasts.descriptionRequired',
  UNKNOWN_CUSTOMER: () => 'time-tracker:toasts.unknownCustomer',
  ADMIN_TOKEN_INVALID: (isAdmin) =>
    isAdmin ? 'time-tracker:toasts.accountRevoked.admin' : 'time-tracker:toasts.accountRevoked.member',
  NOT_CONNECTED: (isAdmin) =>
    isAdmin ? 'time-tracker:toasts.notConnected.admin' : 'time-tracker:toasts.notConnected.member',
  NOT_ACTIVATED: (isAdmin) =>
    isAdmin ? 'time-tracker:toasts.notActivated.admin' : 'time-tracker:toasts.notActivated.member',
  // Unlike every fixed-wording code above, the tracker's own explanation is unavoidable
  // here rather than translated away: this is whatever business rule the account's Kimai
  // admin configured (a maximum entry duration, a required custom field, ...), so Kerika
  // cannot know it ahead of time in any language. Only the sentence around it is
  // translated - see `toasts.validationFailed`.
  VALIDATION_FAILED: () => 'time-tracker:toasts.validationFailed',
};

/**
 * Says something when an operation fails, rather than failing in silence.
 *
 * Stop, edit, discard and save-settings previously swallowed every code they were not
 * expecting: the call failed, the optimistic write rolled back, and nothing on screen said
 * why - so a timer that would not stop looked like a click that had not registered. That
 * is also how a broken provider integration stayed invisible through a whole test pass.
 *
 * @param {Object} state Redux state
 * @param {String} accountId the account the operation was for - decides the wording
 * @param {String} code the server's error code
 * @param {String} [fallbackKey] what to say when the code is not one we recognise; omit
 *        where the caller already shows the error itself, as the settings dialog does
 * @param {String} [message] the server's own explanation, forwarded as-is for the codes whose
 *        wording is never known ahead of time (`VALIDATION_FAILED`, `PROVIDER_UNAVAILABLE`);
 *        unused, and harmless to pass, for every other code
 * @returns {Boolean} whether a toast was shown
 */
const showErrorToastForCode = (state, accountId, code, fallbackKey, message) => {
  try {
    const resolve = TIMER_ERROR_TOASTS[code];
    const key = resolve ? resolve(isAccountAdmin(state, accountId)) : fallbackKey;
    if (!key) {
      return false;
    }
    // Several of these name the tracker, so the interpolation has to be supplied - without
    // it i18next renders the placeholder verbatim and the user is told Kerika could not
    // reach "{{provider}}".
    safeShowErrorToast(key, { provider: providerName(state, accountId), message });
    return true;
  } catch (error) {
    // Everything here runs *inside* a saga's catch block, where an escaping throw does not
    // merely lose the message: redux-saga tears down the root saga with it, and every
    // takeEvery in this module stops for the rest of the session - so a failed timer edit
    // would silently disable connecting, starting, stopping and the lot. Resolving who is
    // an admin and which provider is connected both read selectors that can be handed a
    // value they did not expect, so the whole resolution is guarded, not just the toast.
    console.warn('Time tracker > could not report the failure: ', error);
    return false;
  }
};

/** @returns {String} the connected tracker's display name, for messages that name it. */
const providerName = (state, accountId) => get(selectors.provider(state, accountId), 'name') || '';

/** @returns {Boolean} whether this user administers that account - who a message is worded for. */
const isAccountAdmin = (state, accountId) =>
  manageAccountSelectors.isAccountAdmin({ state, accountId });


/**
 * Subscribes, for every account the user can reach, to that account's
 * `TimeTrackerAccountSettings`, the user's own `TimeTrackerAccountUserSettings`, and their
 * running `ActiveTimer`. Runs for the whole app session (not gated to one page) since the
 * header pill and board-card chip need to be live everywhere.
 *
 * Every account, not just the one in the url. A running timer surfaces on Home and
 * Explore, which carry no account in their path, and the pill needs that timer's account
 * settings to know which tracker it belongs to - so on a reload there the provider logo
 * simply never appeared. Scoping the settings to the url meant the one page that shows
 * timers from *anywhere* could resolve the details of none of them. The active-timer query
 * beside it already spans the same list, so this adds two document reads per account and
 * no new round of discovery.
 *
 * Keyed on the user alone as a result: there is nothing left that depends on which account
 * is being looked at, so navigating between accounts no longer re-subscribes anything, and
 * the whole "did the url and the auth state happen to arrive together" race is gone rather
 * than merely widened.
 */
function* load() {
  const state = yield select();
  const userId = auth.selectors.currentUserId(state);
  if (!userId) {
    return;
  }

  const accounts = auth.selectors.accessibleAccounts(state) || [];
  // Nothing to subscribe to yet - leave `lastLoadedUserId` alone so a later trigger, once
  // the account list has arrived, does the work rather than finding it already claimed.
  if (!accounts.length || lastLoadedUserId === userId) {
    return;
  }

  firestoreRedux.cancelQueryByRequester(ACTIVE_TIMERS_REQUESTER);
  const userIdWo = getIdWoPrefix({ id: userId, prefix: 'usr_' });
  for (const accId of accounts) {
    firestoreRedux.query('active-timers', {
      requesterId: ACTIVE_TIMERS_REQUESTER,
      id: selectors.activeTimersQueryId(userId, accId),
      where: [
        ['accountId', '==', accId],
        ['userId', '==', userId],
      ],
    });

    const accountIdWo = getIdWoPrefix({ id: accId, prefix: 'acc_' });
    // Explicit query ids (rather than the library's generated ones) so `timersLoaded`
    // can watch these settle - a dialog restored from the url must not act on a missing
    // document that is merely still in flight.
    firestoreRedux.getDocById(selectors.accountSettingsPath(accId), `ttas_${accountIdWo}`,
      { requesterId: ACTIVE_TIMERS_REQUESTER, id: selectors.accountSettingsQueryId(accId) });
    firestoreRedux.getDocById(`accounts/${accId}/time-tracker-account-user-settings`, `ttaus_${accountIdWo}_${userIdWo}`,
      { requesterId: ACTIVE_TIMERS_REQUESTER, id: selectors.userSettingsQueryId(accId) });
  }
  lastLoadedUserId = userId;
}

/**
 * Starts a timer when a card the current user owns is moved to In Progress, if the account
 * has asked for that.
 *
 * Watches the card's own update action rather than adding time-tracker code to
 * `card-details`: the rule is a time-tracker preference and belongs with the rest of the
 * feature, and the status change is a plain fact this module can observe.
 *
 * Starts only for a card assigned to the person making the change. Moving a card to In
 * Progress is the act of picking work up, but only if it is your work to pick up: an
 * unassigned card, or somebody else's, does not start your timer.
 *
 * It does not check what the status was before. `updateAttrs` carries the new value while
 * the stored doc may or may not have been locally written yet, depending on which saga ran
 * first, so a before/after comparison here would be a race. Re-selecting In Progress on a
 * card that is already being timed is caught by the running-timer check instead.
 *
 * A timer already running on *another* card is left to `startSaga`, which offers the stop
 * dialog exactly as it does for a start the user asked for by hand.
 */
function* startTimerOnInProgressSaga(action) {
  if (get(action, 'status') !== 'IN_PROGRESS' || !isStatusOnlyChange(action)) {
    return;
  }
  const state = yield select();
  const accountId = router.selectors.accountId(state);
  if (!accountId || !selectors.isActivated(state, accountId)) {
    return;
  }
  if (!get(selectors.preferences(state, accountId), 'startTimerOnInProgress')) {
    return;
  }

  const cardId = get(action, 'id');
  const card = cardId && firestoreRedux.selectors.doc(state, 'cards', cardId);
  if (!card || !card.boardId) {
    return;
  }

  // The card has to be the mover's own work. Moving somebody else's card along, or an
  // unassigned one, is not a claim to have started working on it - and time logged under
  // your name against a task that is not yours is worse than no timer at all. Membership,
  // not sole ownership: on a card shared by several people, the one who moves it to In
  // Progress is the one who has picked it up.
  const assignments = get(card, 'assignments') || [];
  if (!assignments.includes(auth.selectors.currentUserId(state))) {
    return;
  }

  // Already being timed - nothing to start, and re-starting would abandon the open entry.
  if (get(selectors.active(state, accountId), 'cardId') === cardId) {
    return;
  }

  yield put(actions.start(cardId, card.boardId, accountId));
}

/**
 * Loads the one card a running timer is on.
 *
 * Cards otherwise arrive only through a board's own query, so on Explore, Home, or any
 * other board, the header pill and the Stop Timer dialog had a card id and no title to
 * show for it.
 *
 * Read by id, down the card's full path. An earlier attempt at `getDocById('cards', id)`
 * came back empty and was replaced by a filtered query, but the collection was the
 * problem, not the method: a card lives at `boards/{boardId}/cards`, so bare `cards` is
 * not a document path at all. firestore-redux keys its state by the *last* path segment,
 * so the full path still lands under `cards` - the same bucket the board's own query
 * fills - while reading exactly one document instead of running a query for it.
 */
function* loadCardSaga(action) {
  const state = yield select();
  // Resolved against the card being asked for, not just the newest timer: Home/Explore
  // shows more than one pill, and taking `boardId` off whichever timer started last would
  // point the read at the wrong board and silently return nothing.
  const timer = action.cardId
    ? selectors.activeList(state).find((t) => t.cardId === action.cardId)
    : selectors.active(state, action.accountId);
  const cardId = action.cardId || get(timer, 'cardId');
  const boardId = get(timer, 'boardId');
  if (!cardId || !boardId) {
    return;
  }

  // Already here - loaded by the board this card lives on, or by an earlier timer.
  if (firestoreRedux.selectors.doc(state, 'cards', cardId)) {
    return;
  }

  // One card at a time: a timer that moved on leaves nothing subscribed behind it.
  firestoreRedux.cancelQueryByRequester(ACTIVE_TIMER_CARD_REQUESTER);
  firestoreRedux.getDocById(`boards/${boardId}/cards`, cardId, {
    id: `time-tracker-card-${cardId}`,
    requesterId: ACTIVE_TIMER_CARD_REQUESTER,
  });
}

/* ==================== Done or Trash ==================== */

/**
 * Keeps a live read on the card of every running timer, so a move into Done or Trash
 * reaches the app on any page, not only on the card's own board.
 *
 * Not `loadCardSaga`: it keeps one card at a time, so with timers in two accounts the
 * second would cancel the first; and it skips a card already in the store, which goes
 * stale once its board's query is gone.
 */
function* watchTimedCardsSaga() {
  try {
    const state = yield select();
    const timers = selectors.activeList(state).filter((timer) => timer.cardId && timer.boardId);
    const cardIds = timers.map((timer) => timer.cardId);

    watchedTimedCardIds
      .filter((cardId) => !cardIds.includes(cardId))
      .forEach((cardId) => firestoreRedux.cancelQueryByRequester(`${TIMED_CARD_REQUESTER_PREFIX}${cardId}`));

    timers
      .filter((timer) => !watchedTimedCardIds.includes(timer.cardId))
      .forEach((timer) => {
        firestoreRedux.getDocById(`boards/${timer.boardId}/cards`, timer.cardId, {
          id: `${TIMED_CARD_REQUESTER_PREFIX}${timer.cardId}`,
          requesterId: `${TIMED_CARD_REQUESTER_PREFIX}${timer.cardId}`,
        });
      });

    watchedTimedCardIds = cardIds;
  } catch (error) {
    console.error('Time tracker > watching the timed cards failed due to this: ', error);
  }
}

/**
 * Opens the Stop Timer dialog for a running timer whose card was moved into Done or Trash,
 * one at a time, oldest move first. Waits while any time-tracker dialog is in the url; the
 * url change that closes it runs this again.
 */
function* promptDoneOrTrashMoveSaga() {
  try {
    const state = yield select();
    if (!selectors.timersLoaded(state)) {
      return;
    }
    const timerAction = get(state, 'router.page.params.timer-action') || get(state, 'router.dialog.params.timer-action');
    if (timerAction) {
      return;
    }

    const dueMove = selectors.activeList(state)
      .map((timer) => selectors.doneOrTrashMove(state, timer.cardId))
      .filter(Boolean)
      .sort((move1, move2) => move1.movedAt - move2.movedAt)[0];
    if (!dueMove) {
      return;
    }
    yield call(router.actions.setQueryParams, { 'timer-action': 'stop', 'timer-card': dueMove.cardId }, true);
  } catch (error) {
    console.error('Time tracker > prompting to stop a moved card failed due to this: ', error);
  }
}

/**
 * Records the card's current move as answered on this device, and drops the entries of
 * timers no longer running.
 */
function* dismissDoneOrTrashMoveSaga(action) {
  try {
    const state = yield select();
    const move = selectors.doneOrTrashMove(state, action.cardId);
    if (!move) {
      return;
    }
    const runningTimerIds = selectors.activeList(state).map((timer) => timer.id);
    const dismissedMoves = Object.fromEntries(
      Object.entries(selectors.dismissedMoves(state)).filter(([timerId]) => runningTimerIds.includes(timerId))
    );
    dismissedMoves[move.timerId] = move.movedAt;
    yield put(actions._setDismissedMoves(dismissedMoves));
  } catch (error) {
    console.error('Time tracker > dismissing a moved card failed due to this: ', error);
  }
}

/* ==================== Connection ==================== */

// The three 400s `PUT /connect` can answer with - bad URL, key rejected, key not an
// admin key - all mean "fix the form and try again", so the dialog handles all three
// inline rather than falling through to a toast. See openapi.yaml.
// Kept inline in the dialog rather than raised as a toast: every one of these means
// "what you typed is wrong", and the form the user must correct is already on screen.
// PROVIDER_ALREADY_CONNECTED and PROVIDER_NOT_SUPPORTED join them because they are
// answers about the choice being submitted, not about the account's health.
const CONNECT_INLINE_ERROR_CODES = ['INVALID_BASE_URL', 'INVALID_API_TOKEN', 'NOT_ADMIN_TOKEN',
  'PROVIDER_ALREADY_CONNECTED', 'PROVIDER_NOT_SUPPORTED'];

/**
 * Connects or reconnects the account. Holds `requests.connect` at `IN_PROGRESS` for
 * the dialog's verifying state; on `FAILED` with one of `CONNECT_INLINE_ERROR_CODES`
 * the dialog stays open and renders the error inline. The `TimeTrackerAccountSettings`
 * doc arrives by Firestore subscription - this saga does not write it.
 *
 * Deliberately no optimistic local write here (unlike disconnect/settings/stop/
 * update/discard below): the outcome genuinely isn't known until the server
 * verifies the credentials, which is exactly what the dialog's "Verifying..."
 * state is for - writing `status: CONNECTED` ahead of that would show success and
 * then flicker back to failure for the common case of a mistyped key.
 */
function* connectSaga(action) {
  const state = yield select();
  const accountId = action.accountId || router.selectors.accountId(state);
  // Captured before the call: a reconnect is finished once the key is replaced, while a
  // first connect still owes the admin the activate step.
  const wasRevoked = selectors.status(state, accountId) === 'REVOKED';
  try {
    yield call(callApi, `/timetracker/${accountId}/connect`, {
      method: 'PUT',
      // Flat baseUrl/apiToken, not a nested `credentials` object, and the provider id
      // uppercased - both match the real API's `Provider` enum and connect schema, not
      // the connect dialog's own field names (`host`/`apiKey`, provider id `'kimai'`).
      body: {
        provider: (action.provider || '').toUpperCase(),
        baseUrl: get(action.credentials, 'host'),
        apiToken: get(action.credentials, 'apiKey'),
        // A reconnect is by definition replacing the tracker already on the account, and
        // the server refuses that with PROVIDER_ALREADY_CONNECTED unless told the switch is
        // meant. A first connect has nothing to replace and must not claim otherwise.
        confirmProviderSwitch: selectors.status(state, accountId) !== 'NOT_CONNECTED',
      },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._connectSuccess(accountId));

    // Kept for the activate step, which may link this admin to the account with it. A
    // reconnect never reaches that step, so it has nothing to hand over.
    pendingAdminApiKey = wasRevoked ? undefined : get(action.credentials, 'apiKey');

    // The dialog is rendered from the url and unmounts the moment `status` flips, so the
    // hand-off to the activate step belongs here rather than in its own `updated`. It
    // cannot be abandoned midway: the dialog holds itself shut while the call is in
    // flight.
    yield call(router.actions.setQueryParams, { 'timer-action': wasRevoked ? null : 'activate' }, false);
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._connectFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error) || CONNECT_INLINE_ERROR_CODES.includes(code)) {
      return;
    }
    console.error('Time tracker > connect failed due to this: ', error);
  }
}

/**
 * "Use this admin connection for my time tracking too" - links the admin with the key
 * they entered a step earlier, through the ordinary `connectUser` path so the request,
 * its status and its error handling are the member flow exactly.
 *
 * The key is dropped whether or not it was there: it is good for this one hand-off.
 */
function* useAdminKeyForSelfSaga(action) {
  const apiKey = pendingAdminApiKey;
  pendingAdminApiKey = undefined;
  if (!apiKey) {
    // Only reachable if the admin reloaded between the two steps. Nothing to report -
    // they are asked for a key at their first start, like anyone else.
    return;
  }
  const state = yield select();
  yield put(actions.connectUser(apiKey, action.accountId || router.selectors.accountId(state)));
}

/**
 * Links the current user's personal key. On success, resumes a pending Start Timer
 * if the dialog was opened from one. No optimistic local write, same reasoning as
 * `connectSaga`: the key isn't known-good until the server verifies it.
 */
function* connectUserSaga(action) {
  const state = yield select();
  const accountId = action.accountId || router.selectors.accountId(state);
  try {
    yield call(callApi, `/timetracker/${accountId}/user-connect`, {
      method: 'PUT',
      body: { apiToken: action.apiKey },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._connectUserSuccess(accountId));
    yield call(resumePendingStart, accountId);
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._connectUserFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error) || code === 'INVALID_API_TOKEN') {
      return;
    }
    console.error('Time tracker > connectUser failed due to this: ', error);
  }
}

/**
 * Turns the integration off. Optimistically deletes the local
 * `TimeTrackerAccountSettings` doc first - same as every other Firestore-backed
 * write in this codebase (e.g. `manage-account`'s member-removal flow) - so the
 * section drops to `NOT_CONNECTED` immediately rather than waiting a round trip.
 * Reverted on a real failure; left alone on a network error, since then the
 * request's own outcome is unknown and presuming failure would be wrong.
 */
function* disconnectSaga(action) {
  // Nothing left over for an integration that is being torn down.
  pendingAdminApiKey = undefined;
  const state = yield select();
  const accountId = action.accountId || router.selectors.accountId(state);
  const currentDoc = selectors.accountSettings(state, accountId);

  currentDoc && deleteDoc(selectors.accountSettingsPath(accountId), currentDoc.id);

  try {
    yield call(callApi, `/timetracker/${accountId}/disconnect`, { method: 'POST', excludeErrors: HANDLED_STATUSES });
    yield put(actions._disconnectSuccess(accountId));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._disconnectFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }
    currentDoc && saveDoc(selectors.accountSettingsPath(accountId), currentDoc);
    if (code === 'TIMER_ALREADY_RUNNING') {
      safeShowErrorToast('time-tracker:toasts.disconnectBlocked');
      return;
    }
    console.error('Time tracker > disconnect failed due to this: ', error);
  }
}

/**
 * Saves account preferences. Optimistically writes the new `preferences` onto the
 * local `TimeTrackerAccountSettings` doc first, reverted on a real failure (not on
 * a network error - see `disconnectSaga`).
 */
function* updateSettingsSaga(action) {
  const state = yield select();
  const accountId = action.accountId || router.selectors.accountId(state);
  const currentDoc = selectors.accountSettings(state, accountId);

  currentDoc && saveDoc(selectors.accountSettingsPath(accountId), { ...currentDoc, preferences: action.preferences });

  try {
    yield call(callApi, `/timetracker/${accountId}/settings`, {
      method: 'PUT',
      body: { preferences: action.preferences },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._updateSettingsSuccess(accountId));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._updateSettingsFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }
    currentDoc && saveDoc(selectors.accountSettingsPath(accountId), currentDoc);
    // No fallback: the settings dialog renders its own inline error and stays open, so an
    // unrecognised code is already visible there. Only the codes that explain themselves
    // better as a sentence get a toast.
    showErrorToastForCode(state, accountId, code, undefined, error && error.message);
    console.error('Time tracker > updateSettings failed due to this: ', error);
  }
}

/* ==================== Timer ==================== */

/**
 * Starts a timer, and owns every reason a start can be blocked. Before calling the
 * server, reads `active(state)`; if it is set and its `cardId` differs, stores
 * `pendingStart` and opens the stop dialog for the running card instead of calling.
 * The server can answer the same way (`TIMER_ALREADY_RUNNING`) when the client's
 * `active` was stale - both paths converge on the same dialog.
 *
 * No optimistic local write here either (see `connectSaga`): the first-ever timer
 * on a Board/Card provisions server-side and the resulting `ActiveTimer` shape
 * isn't fully known client-side ahead of time. `time-tracker-chip`'s `starting`
 * input carries the loading feedback instead.
 */
function* startSaga(action) {
  const state = yield select();

  const accountId = timerAccountId(state, action.accountId);
  const runningTimer = selectors.active(state, accountId);
  if (runningTimer && runningTimer.cardId !== action.cardId) {
    yield put(actions._setPendingStart(action.cardId, action.boardId));
    yield put(actions._startDeferred(accountId));
    yield call(openStopDialogForBlockedStart);
    return;
  }

  // No personal key yet, or it's already known (via Firestore) to be revoked -
  // resolve before ever calling the server, same as the running-timer check above.
  //
  // Skipped for a resumed start: this reads `TimeTrackerAccountUserSettings` from
  // Firestore, which has not propagated in the moment after `PUT /user-connect`
  // returns, so re-checking here would re-open the dialog the user just completed.
  // The server already confirmed the key; if it disagrees it answers `USER_NOT_CONNECTED`
  // or `USER_TOKEN_INVALID` below and that path opens the dialog properly.
  // Always a first-time `connect`: a personal key that has stopped working leaves nothing
  // behind in Firestore to notice - there is no stored status on this document - so the
  // only thing this pre-check can tell is whether a key was ever linked. A key that has
  // gone bad surfaces as `USER_TOKEN_INVALID` from the call below, which opens the
  // reconnect variant properly.
  if (!action.resumed && !selectors.isUserConnected(state, accountId)) {
    yield put(actions._setPendingStart(action.cardId, action.boardId));
    yield put(actions._startDeferred(accountId));
    yield call(openUserConnectDialogFor, 'connect', accountId);
    return;
  }

  // Who to blame in a toast - `ADMIN_TOKEN_INVALID`, `NOT_CONNECTED` and `NOT_ACTIVATED`
  // are all "the admin needs to fix Settings", worded differently for the admin who can
  // act on it versus the member who has to ask.
  const isAdmin = manageAccountSelectors.isAccountAdmin({ state, accountId });

  try {
    yield call(callApi, `/timetracker/${accountId}/timer/start`, {
      method: 'POST',
      body: { cardId: action.cardId, boardId: action.boardId },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._startSuccess(accountId));
    // Spawned, not forked: `takeLeading` would hold every later start until it finished.
    yield spawn(alignStartWithLastEntry, accountId, action.cardId, action.stoppedAt);
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._startFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }

    if (code === 'TIMER_ALREADY_RUNNING') {
      yield put(actions._setPendingStart(action.cardId, action.boardId));
      yield call(openStopDialogForBlockedStart);
      return;
    }

    // The server found there was never a personal key, or that the one on file has stopped
    // working, before the client's Firestore subscription caught up - same resolution as
    // the pre-check above. Unlike every other operation this one *is* replayed once the
    // key is back, since the click that asked for it said everything it needs.
    if (code === 'USER_NOT_CONNECTED' || code === 'USER_TOKEN_INVALID') {
      yield put(actions._setPendingStart(action.cardId, action.boardId));
      yield call(openUserConnectDialogForKeyFailure, code, accountId);
      return;
    }

    // Everything left is a sentence, not a flow - including PROVIDER_UNAVAILABLE, which
    // used to fall past all of this to a console line, so a start that failed because the
    // tracker was unreachable looked exactly like a click that never registered.
    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.startFailed', error && error.message);
    console.error('Time tracker > start failed due to this: ', error);
  }
}

/**
 * Raises the stop dialog by putting it in the url - `kerika-pwa` renders it from there,
 * importing the module on demand. No `timer-card`: a blocked start means the account's
 * own running timer is the one in the way, which is what the dialog falls back to.
 */
function* openStopDialogForBlockedStart() {
  yield call(router.actions.setQueryParams, { 'timer-action': 'stop' }, true);
}

/**
 * Raises the member connect dialog by putting it in the url - `time-tracker-card` renders
 * it from there. Nothing is constructed here, so Back and refresh behave on their own.
 *
 * `timer-account` names the account the key is being asked for, the way `timer-card` names
 * the card for the stop dialog. The dialog cannot work it out for itself: Home and Explore
 * carry no account in their path, and a timer running in one account while the url names
 * another is ordinary - so the url alone gave "Connect to " with the tracker unnamed, and
 * would have sent the key to whichever account happened to be in the path.
 */
function* openUserConnectDialogFor(mode, accountId) {
  yield call(router.actions.setQueryParams, {
    'timer-action': mode === 'reconnect' ? 'user-reconnect' : 'user-connect',
    // `null` removes it rather than leaving `timer-account=` behind on a call that has
    // no account to name.
    'timer-account': accountId || null,
  }, true);
}

/**
 * A personal-key failure has one way out, and it is a dialog rather than a sentence: the
 * key was never linked (`USER_NOT_CONNECTED`) or has stopped working
 * (`USER_TOKEN_INVALID`). Every read that goes out on the caller's own key can come back
 * with either, so every one of them offers the same resolution.
 *
 * The dialog is the whole message: a caller that raises it says nothing else - no toast,
 * and no console line either. A toast would repeat it, or worse, tell someone to retry an
 * operation that cannot succeed until the dialog in front of them is answered; and a
 * missing or expired personal key is an ordinary state of the integration with a resolution
 * already on screen, not a fault for anyone to go and read logs about.
 *
 * @returns {Boolean} whether the dialog was raised - the caller is done when it was.
 */
function* openUserConnectDialogForKeyFailure(code, accountId) {
  if (code !== 'USER_NOT_CONNECTED' && code !== 'USER_TOKEN_INVALID') {
    return false;
  }
  yield call(openUserConnectDialogFor, code === 'USER_NOT_CONNECTED' ? 'connect' : 'reconnect', accountId);
  return true;
}

/**
 * If a start is waiting on the dialog that just resolved, starts it and clears
 * `pendingStart`. Called after a successful stop/discard/connectUser.
 */
function* resumePendingStart(accountId, stoppedAt) {
  const state = yield select();
  const pending = selectors.pendingStart(state);
  if (!pending) {
    return;
  }
  yield put(actions.clearPendingStart());
  yield put(actions._startResumed(pending.cardId, pending.boardId, accountId, stoppedAt));
}

/**
 * The most the entry just closed may run past the new one's start and still be taken as
 * the tracker's rounding rather than an end of its own: it rounds an end up to the minute.
 */
const START_ALIGN_WINDOW_MS = 60000;

/**
 * Moves a just-started timer's start to where the last closed entry ended, when that entry
 * ends after it.
 *
 * The server times a start from its own clock, while the tracker rounds the end of the
 * entry it just closed up to the whole minute: stopping at 12:12:46 and starting the next
 * timer at once gave one entry ending 12:13:00 and the next beginning 12:12:46. Hence a
 * fresh read - the entry that matters closed a moment ago.
 *
 * A bigger overlap is a parallel timer or an end set by hand, which says nothing about when
 * this one began - except `stoppedAt`, an end the person just set in the future on the
 * stop dialog that held this start. Falls back to it when the read fails.
 *
 * Only once that end has passed: a start still in the future would freeze the running label
 * on "0 sec", so the new timer keeps its own start and the entries overlap by those seconds.
 *
 * Silent whatever happens: a timer starting up to a minute early is what it did before.
 * The start time is the server's, from the `ActiveTimer` it writes - not this device's
 * clock, which can be off by more than the minute being corrected.
 */
function* alignStartWithLastEntry(accountId, cardId, stoppedAt) {
  let endedAt;
  yield put(actions._loadLastEntryStarted(accountId));
  try {
    const entry = yield call(callApi, `/timetracker/${accountId}/timer/last-entry`, {
      method: 'GET',
      // The server errors too, or `request-api` raises its own toast for them.
      excludeErrors: [...HANDLED_STATUSES, 500, 502, 503, 504],
    });
    endedAt = get(entry, 'endedAt');
    yield put(actions._loadLastEntrySuccess(accountId, endedAt ? entry : undefined));
  } catch (error) {
    yield put(actions._loadLastEntryFailed(accountId, { code: (error && error.code) || 'UNKNOWN' }));
  }

  const alignedStartedAt = endedAt || stoppedAt;
  if (!alignedStartedAt) {
    return;
  }
  const startedAt = yield call(waitForStartedAt, accountId, cardId);
  // Never ahead of this device's clock: the label counts from `startedAt` and would sit on
  // "0 sec" until it arrived.
  if (!startedAt || alignedStartedAt <= startedAt || alignedStartedAt > Date.now()) {
    return;
  }
  if (alignedStartedAt - startedAt > START_ALIGN_WINDOW_MS && !(stoppedAt > startedAt)) {
    return;
  }
  yield put(actions.update({ startedAt: alignedStartedAt }, accountId));
}

/**
 * @returns {Number|undefined} the server's start for the card's new timer, once its
 *   `ActiveTimer` has come through Firestore - undefined if it has not within ten seconds.
 */
function* waitForStartedAt(accountId, cardId) {
  for (let attempt = 0; attempt < 40; attempt++) {
    const state = yield select();
    const timer = selectors.active(state, accountId);
    if (timer && timer.cardId === cardId && timer.startedAt) {
      return timer.startedAt;
    }
    yield delay(250);
  }
}

/**
 * Finalizes the entry. Optimistically deletes the local `ActiveTimer` doc first -
 * the chip and header pill disappear immediately instead of waiting on the
 * server. On success closes the dialog, then resumes `pendingStart`. Reverted on
 * a real failure, left alone on a network error (see `disconnectSaga`).
 */
function* stopSaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  const currentDoc = selectors.active(state, accountId);

  currentDoc && deleteDoc('active-timers', currentDoc.id);

  try {
    yield call(callApi, `/timetracker/${accountId}/timer/stop`, {
      method: 'PUT',
      body: {
        tags: action.tags,
        // Omitted, not sent as `false`, when the caller had no say over it - see
        // `GET /timer/billable`. The entry keeps whatever the tracker's own
        // Client -> Project -> Activity inheritance resolved.
        ...(action.billable === undefined ? {} : { billable: action.billable }),
        description: action.description,
        startedAt: action.startedAt,
        endedAt: action.endedAt,
      },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._stopSuccess(accountId));
    yield call(resumePendingStart, accountId, action.endedAt);
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._stopFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }
    currentDoc && saveDoc('active-timers', currentDoc);
    // No pending-retry to match `pendingStart`: the operation is not replayed once the key
    // is back. Its values were entered against a timer that has been ticking ever since,
    // and re-pressing STOP on a dialog already in front of them is one click.
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }

    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.stopFailed', error && error.message);
    console.error('Time tracker > stop failed due to this: ', error);
  }
}

/**
 * Edits the running entry in place. Optimistically writes the changed fields onto
 * the local `ActiveTimer` doc first, so the chip/header pill reflect a corrected
 * `startedAt` immediately. Never changes running state - a `FAILED` leaves the
 * timer running and the dialog open with an inline error; reverted on a real
 * failure, left alone on a network error.
 */
function* updateSaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  const currentDoc = selectors.active(state, accountId);
  const newDoc = currentDoc && {
    ...currentDoc,
    tags: action.tags,
    // See the stop body below: an absent `billable` means the caller may not change it,
    // so the local copy keeps what it already had rather than being blanked.
    ...(action.billable === undefined ? {} : { billable: action.billable }),
    description: action.description,
    startedAt: action.startedAt,
  };

  newDoc && saveDoc('active-timers', newDoc);

  try {
    yield call(callApi, `/timetracker/${accountId}/timer/update`, {
      method: 'PUT',
      body: {
        tags: action.tags,
        ...(action.billable === undefined ? {} : { billable: action.billable }),
        description: action.description,
        startedAt: action.startedAt,
      },
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._updateSuccess(accountId));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._updateFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }
    currentDoc && saveDoc('active-timers', currentDoc);
    // No pending-retry to match `pendingStart`: the operation is not replayed once the key
    // is back. Its values were entered against a timer that has been ticking ever since,
    // and re-pressing STOP on a dialog already in front of them is one click.
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }

    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.updateFailed', error && error.message);
    console.error('Time tracker > update failed due to this: ', error);
  }
}

/**
 * Throws the running entry away - no provider call, deletes the `ActiveTimer`
 * record only, optimistically first (same as `stopSaga`). Also covers recovery
 * when the entry was already stopped/removed directly in the provider. Resumes
 * `pendingStart` on success, same as stop.
 */
function* discardSaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  const currentDoc = selectors.active(state, accountId);

  currentDoc && deleteDoc('active-timers', currentDoc.id);

  try {
    yield call(callApi, `/timetracker/${accountId}/timer/discard`, { method: 'POST', excludeErrors: HANDLED_STATUSES });
    yield put(actions._discardSuccess(accountId));
    yield call(resumePendingStart, accountId);
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._discardFailed(accountId, { code, message: error && error.message }));
    if (isNetworkError(error)) {
      return;
    }
    currentDoc && saveDoc('active-timers', currentDoc);
    // No pending-retry to match `pendingStart`: the operation is not replayed once the key
    // is back. Its values were entered against a timer that has been ticking ever since,
    // and re-pressing STOP on a dialog already in front of them is one click.
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }

    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.discardFailed', error && error.message);
    console.error('Time tracker > discard failed due to this: ', error);
  }
}

/* ==================== Lookups ==================== */

/**
 * Reads the running entry's billable value and whether this person may change it.
 *
 * No skip-if-already-loaded guard, unlike the two lookups below: the spec is explicit that
 * this is read live from the tracker on every call with the caller's own token and never
 * cached. Both halves can change without anything happening in Kerika - the tracker
 * re-resolves `billable` through Client -> Project -> Activity inheritance, and `editable`
 * is learned by attempting the change, since the tracker exposes no read-only way to ask.
 *
 * Failure is quiet, bar one: the dialog falls back to the timer's own stored `billable` and
 * leaves the toggle alone, which is no worse than before this call existed - and
 * `NO_TIMER_RUNNING` is a perfectly ordinary answer here, since the dialog can be opened
 * from a stale link. A personal-key failure is the exception, since the stop this dialog
 * exists for would fail on it too - see `openUserConnectDialogForKeyFailure`.
 */
function* loadBillableSaga(action) {
  const state = yield select();
  // `timerAccountId`, not the router's alone: this is about the running entry, and on
  // Home there is no account in the url at all - the same reason stop, update and discard
  // resolve it this way. Read from the router only, the request went to `/timetracker//`.
  const accountId = timerAccountId(state, action.accountId);
  if (!accountId) {
    return;
  }
  yield put(actions._loadBillableStarted(accountId));
  try {
    const status = yield call(callApi, `/timetracker/${accountId}/timer/billable`, {
      method: 'GET',
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._loadBillableSuccess(accountId, status));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._loadBillableFailed(accountId, { code }));
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }
    console.error('Time tracker > loadBillable failed due to this: ', error);
  }
}

/**
 * Reads this person's last closed entry from the tracker, to offer its end as a start
 * time for the entry being stopped.
 *
 * Failure is quiet, like `loadBillable`, and with the same exception: the hint is an offer,
 * and a dialog that cannot make it is exactly the dialog as it was before this existed -
 * but a personal key that is missing or rejected is worth raising, since it is the whole
 * dialog that is about to fail, not just its hint. A 204 answers with no body - nothing has
 * ever been closed on the tracker - and is a success with no entry rather than a failure.
 */
function* loadLastEntrySaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  const isPrefetch = action.type === actions.PREFETCH_LAST_ENTRY;
  if (!accountId || (isPrefetch && get(selectors.lastEntry(state, accountId), 'status') === 'IN_PROGRESS')) {
    return;
  }
  yield put(actions._loadLastEntryStarted(accountId));
  try {
    const entry = yield call(callApi, `/timetracker/${accountId}/timer/last-entry`, {
      method: 'GET',
      excludeErrors: HANDLED_STATUSES,
    });
    yield put(actions._loadLastEntrySuccess(accountId, get(entry, 'endedAt') ? entry : undefined));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._loadLastEntryFailed(accountId, { code }));
    if (isPrefetch) {
      return;
    }
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }
    console.error('Time tracker > loadLastEntry failed due to this: ', error);
  }
}

/**
 * Lazy, once per account per page load unless `force` is passed - the Stop Timer
 * dialog forces a refetch every time it opens, so a tag created in the tracker since
 * the last stop shows up without a full page reload.
 *
 * The skip lives in the saga, not the reducer: redux-saga runs reducers before sagas
 * see the action, so if `LOAD_TAGS` itself set `IN_PROGRESS`, this guard would always
 * be looking at the status its own dispatch just wrote and would skip every first call.
 * Hence `_loadTagsStarted`, dispatched only once the guard has passed.
 */
function* loadTagsSaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  if (!accountId) {
    return;
  }
  const existing = selectors.tags(state, accountId);
  if (!action.force && existing && (existing.status === 'IN_PROGRESS' || existing.status === 'SUCCESS')) {
    return;
  }
  yield put(actions._loadTagsStarted(accountId));
  try {
    const list = yield call(callApi, `/timetracker/${accountId}/tags`, { method: 'GET', excludeErrors: HANDLED_STATUSES });
    // The API returns plain tag names (`string[]`) - `time-tracker-tags-select` reads
    // `itemLabel: 'name'`/`itemValue: 'id'`, so each name doubles as its own id here.
    yield put(actions._loadTagsSuccess(accountId, (list || []).map((name) => ({ id: name, name }))));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._loadTagsFailed(accountId, { code }));
    if (isNetworkError(error)) {
      return;
    }
    // No pending-retry to match `pendingStart`: the lookup is not replayed once the key is
    // back. Re-pressing STOP on a dialog already in front of them is one click.
    if (yield call(openUserConnectDialogForKeyFailure, code, accountId)) {
      return;
    }

    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.tagsFailed');
    console.error('Time tracker > loadTags failed due to this: ', error);
  }
}

/** Lazy, once per account per page load. `force` refetches - see `loadTagsSaga` on why the skip lives in the saga. */
function* loadCustomersSaga(action) {
  const state = yield select();
  const accountId = timerAccountId(state, action.accountId);
  if (!accountId) {
    return;
  }
  const existing = selectors.customers(state, accountId);
  if (!action.force && existing && (existing.status === 'IN_PROGRESS' || existing.status === 'SUCCESS')) {
    return;
  }
  yield put(actions._loadCustomersStarted(accountId));
  try {
    const list = yield call(callApi, `/timetracker/${accountId}/customers`, { method: 'GET', excludeErrors: HANDLED_STATUSES });
    yield put(actions._loadCustomersSuccess(accountId, list || []));
  } catch (error) {
    const code = (error && error.code) || 'UNKNOWN';
    yield put(actions._loadCustomersFailed(accountId, { code }));
    if (isNetworkError(error)) {
      return;
    }
    showErrorToastForCode(state, accountId, code, 'time-tracker:toasts.customersFailed');
    console.error('Time tracker > loadCustomers failed due to this: ', error);
  }
}

/**
 * Init Saga.
 */
export function* timeTrackerSaga() {
  yield all([
    call(load),
    takeEvery(auth.actions.UPDATE_AUTH, load),
    takeEvery(router.actions.ACCOUNT_CHANGED, load),
    // Also on any navigation: ACCOUNT_CHANGED does not fire when the app simply *lands* on
    // an account url, and UPDATE_AUTH does not fire when a session is restored rather than
    // established - so on a plain reload of a board there were runs where neither trigger
    // ever coincided with both a user and an account being known. `load` is guarded to be
    // a no-op once it has done its work, so the extra trigger costs nothing.
    takeEvery(router.actions.UPDATE_ROUTER, load),
    takeEvery(actions.CONNECT, connectSaga),
    takeEvery(actions.CONNECT_USER, connectUserSaga),
    takeEvery(actions.USE_ADMIN_KEY_FOR_SELF, useAdminKeyForSelfSaga),
    takeEvery(actions.DISCONNECT, disconnectSaga),
    takeEvery(actions.UPDATE_SETTINGS, updateSettingsSaga),
    // Leading, not every: double-clicking Start Timer dispatched two, and no check caught
    // the pair - the client's looks for an `ActiveTimer` and the server's for a running
    // entry, and while the first call is in flight there is neither, so both went through
    // and the tracker was given two entries for the one card. This drops a second start
    // while the first is still running and needs no flag of its own, so a start that never
    // resolves cannot leave every later one blocked.
    takeLeading(actions.START, startSaga),
    takeEvery(CARD_DETAILS_UPDATE_ATTRS, startTimerOnInProgressSaga),
    takeEvery(actions.STOP, stopSaga),
    takeEvery(actions.UPDATE, updateSaga),
    takeEvery(actions.DISCARD, discardSaga),
    takeEvery(actions.LOAD_BILLABLE, loadBillableSaga),
    takeEvery([actions.LOAD_LAST_ENTRY, actions.PREFETCH_LAST_ENTRY], loadLastEntrySaga),
    takeEvery(actions.LOAD_TAGS, loadTagsSaga),
    takeEvery(actions.LOAD_CUSTOMERS, loadCustomersSaga),
    takeEvery(actions.LOAD_CARD, loadCardSaga),
    takeEvery(firestoreRedux.actions.QUERY_SNAPSHOT, watchTimedCardsSaga),
    takeEvery(actions.DISMISS_DONE_OR_TRASH_MOVE, dismissDoneOrTrashMoveSaga),
    // Debounced, not taken at once: Back drops `timer-action` before the dialog's own
    // dismiss is recorded, and deciding in that gap would reopen the move just closed.
    debounce(PROMPT_DEBOUNCE_MS, [
      firestoreRedux.actions.QUERY_SNAPSHOT,
      router.actions.UPDATE_ROUTER,
      actions.SET_DISMISSED_MOVES,
    ], promptDoneOrTrashMoveSaga),
  ]);
}
